/**
 * PlatformExtensions — dedicated admin page for the lextools bundle
 * lifecycle. Commit C of the LexPortal lextools-surfaces miniplan.
 *
 * Surfaces:
 *   1. ACTIVE — currently-promoted bundle (sourced from `GET /api/platform/extensions`
 *      root, which carries both the head fields + the `history[]` array)
 *      with an empty state when no bundle has been promoted yet.
 *   2. AVAILABLE — backend-listed tags (`/api/platform/extensions/available`).
 *      Each row has a Promote button (default activate: 'idle-restart',
 *      with a dropdown to select 'broker-only') + a secondary "Force-apply
 *      (rollback)" button (activate: 'immediate' behind a type-to-confirm
 *      ROLLBACK gate).
 *   3. HISTORY — last 50 promotions (from `/api/platform/extensions`'s
 *      `history[]`).
 *   4. FLEET CONVERGENCE — N/M runtimes on the active version. Reuses
 *      `useRuntimeVersions` (60s poll). Links to `/runtimes` for drill-down.
 *
 * Auth: gated on `requirePlatformAdmin()` server-side; the page assumes
 * the operator JWT carries platform.admin scope.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  History as HistoryIcon,
  Loader2,
  Package,
  RefreshCw,
  ShieldAlert,
  Tag as TagIcon,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { useRuntimeVersions } from "@/hooks/useRuntimeVersions";
import { PlatformApiError } from "@/lib/platform-api";
import { selectConvergenceState } from "@/lib/convergence-state";
import type {
  AvailableExtensionsTag,
  PlatformExtensionsBundleDoc,
} from "@/types/platform";

type ActivateMode = "broker-only" | "idle-restart" | "immediate";

/** Single source of truth for the rollback confirm phrase — mirrors
 *  lextools/scripts/rollback-bundle.sh CLI semantics. */
const ROLLBACK_CONFIRM_PHRASE = "ROLLBACK";

/** Strip trailing whitespace (incl. CR from Windows pastes) before
 *  the strict equality compare. Same shape as the CLI rollback-bundle.sh
 *  guard. Does NOT strip from the first whitespace onwards — that would
 *  let "ROLLBACK now" pass. */
function rollbackPhraseMatches(input: string): boolean {
  return input.replace(/[\s\r]+$/, "") === ROLLBACK_CONFIRM_PHRASE;
}

interface PromoteResult {
  tag: string;
  activate: ActivateMode;
  enqueued: number;
  skipped: number;
}

export function PlatformExtensions() {
  const api = usePlatformApi();
  const [doc, setDoc] = useState<PlatformExtensionsBundleDoc | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState(true);
  const [available, setAvailable] = useState<AvailableExtensionsTag[]>([]);
  const [availableError, setAvailableError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ tag: string; mode: ActivateMode } | null>(null);
  const [promoteResult, setPromoteResult] = useState<PromoteResult | null>(null);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [rollbackTag, setRollbackTag] = useState<string | null>(null);
  const [rollbackConfirm, setRollbackConfirm] = useState("");

  // Fleet convergence via the existing shared 60s-poll hook — single
  // source of truth + no cadence drift vs the runtimes page.
  //
  // ⚠ Take the THIRD-STATE signals, not just the map. An empty `byUserId`
  // means one of four things and only ONE of them is "the fleet has no azure
  // runtimes"; the other three are "I could not look". This consumer used to
  // destructure `byUserId` alone and rendered all four as the confident
  // sentence below — observed on PROD 2026-08-07 saying the fleet was empty
  // while /portal/runtimes listed five healthy azure rows at the same moment
  // (found by `lexiun-ws-box`). See
  // `ai-docs/convergence-card-unreadable-state-miniplan.md`.
  const {
    byUserId,
    available: versionsAvailable,
    error: versionsError,
    initializing: versionsInitializing,
  } = useRuntimeVersions();

  const fetchDoc = useCallback(async () => {
    setDocLoading(true);
    setDocError(null);
    try {
      const r = await api.getExtensionsBundleDoc();
      setDoc(r.data);
    } catch (err) {
      if (err instanceof PlatformApiError && err.code === "NO_BUNDLE_PROMOTED") {
        setDoc(null); // cold state
      } else {
        setDocError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setDocLoading(false);
    }
  }, [api]);

  const fetchAvailable = useCallback(async () => {
    setAvailableError(null);
    try {
      const r = await api.listAvailableExtensionsTags();
      setAvailable(r.data.tags);
    } catch (err) {
      if (err instanceof PlatformApiError && err.code === "BUNDLE_BACKEND_UNAVAILABLE") {
        // ACR backend stub (Phase 6 not yet wired in this env) — show
        // the doc but degrade the available-tags list cleanly.
        setAvailable([]);
        setAvailableError(
          "Bundle backend listing not available in this environment (Phase 6 ACR pull not yet wired). Use the CLI publish-dev.sh / install-bundle.sh path for now.",
        );
      } else {
        setAvailableError(err instanceof Error ? err.message : String(err));
      }
    }
  }, [api]);

  useEffect(() => {
    void fetchDoc();
    void fetchAvailable();
  }, [fetchDoc, fetchAvailable]);

  const activeTag = doc?.tag ?? null;

  // Fleet convergence — count runtimes that report bundle.tag matching
  // the active tag. NULL active → no convergence math; degrade to "—".
  const convergence = useMemo(() => {
    if (!activeTag) return null;
    let onActive = 0;
    let total = 0;
    let differs = 0;
    let pending = 0;
    let unknown = 0;
    for (const r of byUserId.values()) {
      if (r.deployTarget !== "azure") continue; // T1-idle is azure-only
      total++;
      if (r.bundlePending) {
        pending++;
      } else if (r.running?.bundle?.tag === activeTag) {
        onActive++;
      } else if (!r.running?.bundle) {
        unknown++;
      } else {
        differs++;
      }
    }
    return { onActive, total, differs, pending, unknown };
  }, [byUserId, activeTag]);

  // Which state the card is in. Pure + separately tested so the PRECEDENCE is
  // provable: "not looked yet" and "could not look" both outrank any count,
  // because a count taken from an unread map is not evidence of anything.
  const convergenceState = useMemo(
    () =>
      selectConvergenceState({
        hasActiveTag: Boolean(activeTag),
        initializing: versionsInitializing,
        available: versionsAvailable,
        error: versionsError,
        total: convergence?.total ?? 0,
      }),
    [activeTag, versionsInitializing, versionsAvailable, versionsError, convergence],
  );

  async function doPromote(tag: string, mode: ActivateMode) {
    setPromoteError(null);
    setPromoteResult(null);
    setPending({ tag, mode });
    try {
      const r = await api.promoteExtensionsBundle({
        tag,
        activate: mode,
        reason:
          mode === "immediate"
            ? "lexportal — force-apply (rollback)"
            : "lexportal",
        promoted_via: mode === "immediate" ? "rollback" : "lexportal",
      });
      setPromoteResult({
        tag: r.data.tag,
        activate: r.data.activation.activate,
        enqueued: r.data.activation.enqueued,
        skipped: r.data.activation.skipped,
      });
      // Refresh both surfaces so the new ACTIVE row + updated history
      // appear without an operator F5.
      void fetchDoc();
      void fetchAvailable();
    } catch (err) {
      setPromoteError(
        err instanceof Error ? err.message : String(err),
      );
    } finally {
      setPending(null);
    }
  }

  function startRollback(tag: string) {
    setRollbackTag(tag);
    setRollbackConfirm("");
  }

  async function confirmRollback() {
    if (!rollbackTag) return;
    if (!rollbackPhraseMatches(rollbackConfirm)) return;
    await doPromote(rollbackTag, "immediate");
    setRollbackTag(null);
    setRollbackConfirm("");
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Package className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Lextools versions
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Manage the platform's currently-active lextools bundle. Promote
          a new tag (default: idle-restart — preserves in-flight LLM
          turns) or force-apply a prior tag for rollback.
        </p>
      </header>

      {/* ── ACTIVE ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            Active version
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void fetchDoc();
              void fetchAvailable();
            }}
            data-testid="platform-extensions-refresh"
          >
            <RefreshCw className="mr-1 h-3 w-3" /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {docLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : docError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
              Failed to load: {docError}
            </div>
          ) : !doc ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              No lextools bundle has been promoted yet. Use the CLI
              <code className="mx-1 rounded bg-slate-200 px-1 dark:bg-slate-800">
                ./lextools/scripts/install-bundle.sh --tag &lt;tag&gt;
              </code>
              or pick a tag from "Available tags" below.
            </div>
          ) : (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-slate-500">Tag</dt>
              <dd className="font-mono">
                <Badge
                  variant="outline"
                  className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                  data-testid="active-bundle-tag"
                >
                  {doc.tag}
                </Badge>
              </dd>
              <dt className="text-slate-500">sha256</dt>
              <dd className="font-mono text-xs">{doc.sha256.slice(0, 32)}…</dd>
              <dt className="text-slate-500">Promoted</dt>
              <dd>
                {doc.promoted_at} by{" "}
                <span className="font-medium">
                  {doc.promoted_by ?? "(unknown)"}
                </span>{" "}
                via{" "}
                <span className="text-xs text-slate-500">
                  {doc.promoted_via ?? "(unknown)"}
                </span>
              </dd>
              {doc.history[0]?.reason ? (
                <>
                  <dt className="text-slate-500">Reason</dt>
                  <dd className="italic">{doc.history[0].reason}</dd>
                </>
              ) : null}
            </dl>
          )}
        </CardContent>
      </Card>

      {/* ── FLEET CONVERGENCE ──────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fleet convergence</CardTitle>
        </CardHeader>
        <CardContent>
          {!convergence ? (
            <p className="text-sm text-slate-500">
              No active bundle to converge against.
            </p>
          ) : convergenceState.kind === "loading" ? (
            /* Not looked yet. Asserting anything about the fleet here is the
               bug — the hook publishes `initializing` precisely so this notice
               never flashes a false verdict. */
            <p className="text-sm text-slate-500" data-testid="convergence-loading">
              Reading fleet convergence…
            </p>
          ) : convergenceState.kind === "unreadable" ? (
            /* COULD NOT LOOK — endpoint absent/unreachable, or faulted. Either
               way this is NOT a statement about how many runtimes exist. */
            <p className="text-sm text-amber-600 dark:text-amber-500" data-testid="convergence-unreadable">
              Fleet convergence cannot be read —{" "}
              {convergenceState.reason === "endpoint-unreachable"
                ? "the runtime-versions endpoint is not reachable."
                : "the runtime-versions endpoint faulted."}{" "}
              {convergenceState.detail && (
                <span className="font-mono text-xs">{convergenceState.detail}</span>
              )}{" "}
              This is <strong>not</strong> a statement that the fleet is empty;{" "}
              <Link to="/runtimes" className="underline">/portal/runtimes</Link> remains authoritative.
            </p>
          ) : convergenceState.kind === "empty-fleet" ? (
            /* The ONE case this copy is true for: a successful, available,
               non-erroring read that genuinely found no azure rows. */
            <p className="text-sm text-slate-500" data-testid="convergence-empty-fleet">
              No azure runtimes registered. The fleet convergence metric
              applies to azure-mode runtimes only (T1-idle is azure-only;
              docker rows use the dev <code>?force=1</code> escape hatch).
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <span data-testid="convergence-on-active">
                <strong className="text-emerald-700 dark:text-emerald-400">
                  {convergence.onActive}/{convergence.total}
                </strong>{" "}
                runtimes on active version
              </span>
              {convergence.pending > 0 ? (
                <span className="text-amber-700 dark:text-amber-400">
                  {convergence.pending} pending T1-idle
                </span>
              ) : null}
              {convergence.differs > 0 ? (
                <span className="text-amber-700 dark:text-amber-400">
                  {convergence.differs} differ
                </span>
              ) : null}
              {convergence.unknown > 0 ? (
                <span className="text-slate-500">
                  {convergence.unknown} unknown
                </span>
              ) : null}
              <Link
                to="/runtimes"
                className="ml-auto text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
              >
                Show fleet →
              </Link>
            </div>
          )}
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Refreshes every 60 seconds via the shared useRuntimeVersions
            poll. Azure runtimes only (T1-idle is azure-only).
          </p>
        </CardContent>
      </Card>

      {/* ── PROMOTE FEEDBACK ───────────────────────────────────────── */}
      {promoteResult ? (
        <Card className="border-emerald-300 dark:border-emerald-800">
          <CardContent className="flex items-start gap-2 p-4 text-sm">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <div>
              <div>
                Promoted <code>{promoteResult.tag}</code> ·{" "}
                activate=<code>{promoteResult.activate}</code>
              </div>
              <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                Activation fan-out: enqueued={promoteResult.enqueued} ·
                skipped={promoteResult.skipped}.{" "}
                {promoteResult.activate === "idle-restart"
                  ? "Each runtime restarts when idle (up to 24h)."
                  : promoteResult.activate === "immediate"
                    ? "Each runtime force-restarts now."
                    : "Bundle delivered; no restart triggered."}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
      {promoteError ? (
        <Card className="border-red-300 dark:border-red-800">
          <CardContent className="flex items-start gap-2 p-4 text-sm text-red-700 dark:text-red-300">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Promote failed: {promoteError}</span>
          </CardContent>
        </Card>
      ) : null}

      {/* ── AVAILABLE TAGS ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TagIcon className="h-4 w-4" /> Available tags
          </CardTitle>
        </CardHeader>
        <CardContent>
          {availableError ? (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300">
              {availableError}
            </div>
          ) : null}
          {available.length === 0 ? (
            <p className="text-sm text-slate-500">No tags listed.</p>
          ) : (
            <ul className="divide-y divide-slate-200 dark:divide-slate-700">
              {available.map((t) => {
                const isActive = t.tag === activeTag;
                const inFlight =
                  pending?.tag === t.tag ? pending.mode : null;
                return (
                  <li
                    key={t.tag}
                    className="flex flex-wrap items-center gap-3 py-2 text-sm"
                    data-testid={`available-tag-${t.tag}`}
                  >
                    <code className="font-mono">{t.tag}</code>
                    {isActive ? (
                      <Badge
                        variant="outline"
                        className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                      >
                        ACTIVE
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-500">
                        {(t.byteLength / 1024).toFixed(1)} KiB
                      </span>
                    )}
                    {!isActive ? (
                      <div className="ml-auto flex items-center gap-2">
                        <PromoteDropdown
                          disabled={!!inFlight}
                          onPromote={(mode) => doPromote(t.tag, mode)}
                          inFlightMode={inFlight}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/50"
                          onClick={() => startRollback(t.tag)}
                          disabled={!!inFlight}
                          data-testid={`force-apply-${t.tag}`}
                        >
                          <ShieldAlert className="mr-1 h-3 w-3" />
                          Force-apply (rollback)
                        </Button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ── HISTORY ────────────────────────────────────────────────── */}
      {doc?.history && doc.history.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <HistoryIcon className="h-4 w-4" /> History (last{" "}
              {doc.history.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-slate-200 text-xs dark:divide-slate-700">
              {doc.history.map((h, i) => (
                <li
                  key={`${h.tag}-${h.promoted_at}-${i}`}
                  className="flex flex-wrap items-baseline gap-2 py-1.5"
                >
                  <code className="font-mono">{h.tag}</code>
                  <span className="text-slate-500">·</span>
                  <span>{h.promoted_at}</span>
                  <span className="text-slate-500">·</span>
                  <span>{h.promoted_by}</span>
                  <span className="text-slate-500">·</span>
                  <span>{h.promoted_via}</span>
                  {h.reason ? (
                    <>
                      <span className="text-slate-500">·</span>
                      <span className="italic">{h.reason}</span>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {/* ── ROLLBACK CONFIRM DIALOG ────────────────────────────────── */}
      {rollbackTag ? (
        <RollbackDialog
          tag={rollbackTag}
          confirm={rollbackConfirm}
          onConfirmChange={setRollbackConfirm}
          onCancel={() => {
            setRollbackTag(null);
            setRollbackConfirm("");
          }}
          onConfirm={() => void confirmRollback()}
        />
      ) : null}
    </div>
  );
}

// ── Sub-components (inline; layout-heavy not logic-heavy) ────────────

function PromoteDropdown({
  disabled,
  onPromote,
  inFlightMode,
}: {
  disabled: boolean;
  onPromote: (mode: ActivateMode) => void;
  inFlightMode: ActivateMode | null;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Dismiss the dropdown on outside-click + Escape — standard React
  // pattern. mousedown so dismissal fires BEFORE the menu-item click
  // (otherwise the menu item's onClick would race with the close).
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex">
        <Button
          size="sm"
          onClick={() => onPromote("idle-restart")}
          disabled={disabled}
          className="rounded-r-none"
          data-testid="promote-idle-restart"
        >
          {inFlightMode === "idle-restart" ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : null}
          Promote
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          className="rounded-l-none border-l-0 px-2"
          aria-label="More activation options"
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
      </div>
      {open ? (
        <div className="absolute right-0 z-10 mt-1 w-56 rounded-md border border-slate-200 bg-white shadow-md dark:border-slate-700 dark:bg-slate-900">
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
            onClick={() => {
              setOpen(false);
              onPromote("idle-restart");
            }}
          >
            <strong>idle-restart</strong> (default)
            <div className="text-[10px] text-slate-500">
              Restart each runtime when idle. Preserves in-flight LLM turns.
            </div>
          </button>
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
            onClick={() => {
              setOpen(false);
              onPromote("broker-only");
            }}
            data-testid="promote-broker-only"
          >
            <strong>broker-only</strong>
            <div className="text-[10px] text-slate-500">
              Stage to disk only. No restart. Active on next natural restart.
            </div>
          </button>
        </div>
      ) : null}
    </div>
  );
}

function RollbackDialog({
  tag,
  confirm,
  onConfirmChange,
  onCancel,
  onConfirm,
}: {
  tag: string;
  confirm: string;
  onConfirmChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const matches = rollbackPhraseMatches(confirm);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm rollback"
    >
      <div className="w-full max-w-lg space-y-4 rounded-lg border border-red-300 bg-white p-6 shadow-lg dark:border-red-800 dark:bg-slate-900">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-red-800 dark:text-red-300">
          <ShieldAlert className="h-5 w-5" /> Force-apply rollback
        </h2>
        <div className="space-y-2 text-sm">
          <p>
            You are about to force-restart all azure runtimes to adopt
            bundle <code className="font-mono">{tag}</code>.
          </p>
          <p>
            <strong>Mode:</strong> T1-forced (bypasses the idle gate;
            in-flight LLM turns at the moment of restart will be lost,
            NOT replayed).
          </p>
          <p>
            <strong>Audit:</strong> trigger will record as{" "}
            <code>t1-forced</code>;{" "}
            <code>extensions.bundle.activation-enqueued</code> will
            record <code>activate: "immediate"</code>.
          </p>
        </div>
        <label className="block text-sm">
          Type <code className="font-mono">{ROLLBACK_CONFIRM_PHRASE}</code> to confirm:
          <input
            type="text"
            autoFocus
            value={confirm}
            onChange={(e) => onConfirmChange(e.target.value)}
            onKeyDown={(e) => {
              // Enter-to-confirm when the phrase matches; Escape cancels.
              // Matches form-style operator UX expectations.
              if (e.key === "Enter" && matches) {
                e.preventDefault();
                onConfirm();
              } else if (e.key === "Escape") {
                e.preventDefault();
                onCancel();
              }
            }}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 font-mono dark:border-slate-600 dark:bg-slate-800"
            data-testid="rollback-confirm-input"
          />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!matches}
            className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            data-testid="rollback-confirm"
          >
            Confirm rollback
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PlatformExtensions;
